import { useEffect } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useAppDispatch, useAppSelector } from '../flux/stores/hooks';
import { fetchMyOrganization } from '../flux/actions/organizationThunks';
import { useApiAccessToken } from './useApiAccessToken';

/**
 * Ensures `state.organization` is populated exactly once per session, whichever
 * component mounts first (e.g. `SideNav` on any authenticated page, or
 * `Organization` if the user lands there directly). Safe to call unconditionally
 * from multiple components — no-ops while signed out, and skips the fetch once
 * the store already has an answer (an org, or a confirmed "floating, no org"
 * result).
 */
export function useEnsureOrganizationLoaded() {
    const dispatch = useAppDispatch();
    const { getAccessToken } = useApiAccessToken();
    const isAuthenticated = useIsAuthenticated();
    const org = useAppSelector(state => state.organization.data);
    const notMember = useAppSelector(state => state.organization.notMember);

    useEffect(() => {
        if (!isAuthenticated || org || notMember) return;

        let cancelled = false;
        (async () => {
            const accessToken = await getAccessToken();
            if (!cancelled && accessToken) {
                dispatch(fetchMyOrganization({ accessToken }));
            }
        })();
        return () => { cancelled = true; };
    }, [dispatch, getAccessToken, isAuthenticated, org, notMember]);
}
